import { useState } from 'react';
import { MapPin, Undo2, Check, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import CardImage from './CardImage';
import CompletionRings from './CompletionRings';
import QuizCard from './QuizCard';
import { normalizeCard } from '../utils/companionContext.js';
import { diffLines, undoState, receiptAge } from '../utils/receiptFormat.js';
import { money, formatPriceSources } from '../utils/money.js';
import { whereLine } from '../utils/entryLabels.js';
import { toBlocks } from '../utils/richText.js';
import { useCompanion } from '../utils/companionStore.js';
import { useT } from '../utils/i18n';
import { formatDate, formatDateTime } from '../utils/dates.js';
import { entryCad, cadEstimate, originalValues } from '../utils/cadValue.js';

// Renderers for the typed cards inside a turn. Every number came from the
// server; these components only lay it out.

// Safe rich text: paragraphs, bullets, bold. Nothing else, no innerHTML.
export function RichText({ text }) {
  const blocks = toBlocks(text);
  if (!blocks.length) return null;
  const runs = (rs, key) => rs.map((r, i) => (r.t === 'bold' ? <strong key={`${key}-${i}`}>{r.v}</strong> : <span key={`${key}-${i}`}>{r.v}</span>));
  return (
    <div className="cmp-rich">
      {blocks.map((b, i) => b.type === 'ul'
        ? <ul key={i}>{b.items.map((it, j) => <li key={j}>{runs(it, `${i}-${j}`)}</li>)}</ul>
        : <p key={i}>{runs(b.runs, i)}</p>)}
    </div>
  );
}

// Calendar dates keep their day in every timezone; epoch-era values are unknown.
const dateOrUnknown = (v, t) => formatDate(v) || t('companion.dateUnknown');

// CAD first. The headline is the server's dated CAD estimate for this copy;
// the original quote (amount, currency, source, quote date), the exchange
// rate and any warning sit behind Details. Without an estimate the line says
// so and never promotes the source currency to the headline.
function PriceLine({ e }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const p = e?.price;
  if (!p || typeof p !== 'object') return <div className="cmp-asof">{t('companion.priceUnavailable')}</div>;
  const est = entryCad(e);
  const original = money(p.amount, p.currency);
  if (!est && !original) return <div className="cmp-asof">{t('companion.priceUnavailable')}</div>;
  const when = dateOrUnknown(p.quote_as_of || p.as_of, t);
  return (
    <div className="cmp-entry-price">
      <div className="cmp-price-head">
        <span>{est ? <>{money(est.amount, 'CAD')} <span className="cmp-asof">{t('companion.estimate')}</span></> : <span className="cmp-asof">{t('dash.noCadEstimate')}</span>}</span>
        <button type="button" className="cmp-link" aria-expanded={open} onClick={() => setOpen(o => !o)}>{open ? <ChevronUp size={12} /> : <ChevronDown size={12} />} {t('dash.details')}</button>
      </div>
      {p.warning && <div className="cmp-asof cmp-warn">{String(p.warning)}</div>}
      {open && (
        <div className="cmp-price-details">
          {original && <div>{t('dash.quoteLine', { amount: original, source: p.source || t('dash.unknownSource'), date: when })}{String(p.freshness || '') === 'stale' ? ` · ${t('companion.estimateStale')}` : ''}</div>}
          {est && est.fxSource && <div>{[est.fxSource, formatDate(est.fxAsOf) || null, est.rate ? t('dash.fxRate', { currency: p.currency || '', rate: est.rate.toFixed(4) }) : null].filter(Boolean).join(' · ')}{est.stale ? ` · ${t('companion.estimateStale')}` : ''}</div>}
        </div>
      )}
    </div>
  );
}

export function EntryCard({ data, title, dense = false }) {
  const { t } = useT();
  const { onShowEntry, openSheet } = useCompanion();
  const e = data || {};
  const entryId = e.entry_id ?? e.id;
  const finish = e.variant_label || e.foil_pattern ? `${e.printing || ''} · ${e.variant_label || e.foil_pattern}` : (e.printing || '');
  const where = whereLine(e, t);
  const tag = e.opening_tag || e.tag || e.status;
  const knownTag = ['new_card', 'new_finish', 'duplicate'].includes(tag) ? tag : null;
  return (
    <div className={`cmp-entry ${dense ? 'dense' : ''}`}>
      <div className="cmp-entry-art"><CardImage card={e} style={{ width: '100%', aspectRatio: 0.718, objectFit: 'cover', borderRadius: '6px' }} /></div>
      <div className="cmp-entry-info">
        <div className="cmp-entry-name">{title || e.name || e.printed_name || t('companion.card')}</div>
        <div className="cmp-entry-line">{[e.set_name, e.number ? `#${e.number}` : null, finish].filter(Boolean).join(' · ')}</div>
        {e.condition && <div className="cmp-entry-line">{e.condition}{e.quantity > 1 ? ` · ×${e.quantity}` : ''}</div>}
        {where && <div className="cmp-entry-line"><MapPin size={12} /> {where}</div>}
        <PriceLine e={e} />
        {e.reason && <div className="cmp-entry-why">{e.reason}</div>}
        {knownTag && <span className={`cmp-tag cmp-tag-${knownTag}`}>{t(`companion.tag.${knownTag}`)}</span>}
        <div className="cmp-entry-actions">
          {entryId != null && onShowEntry && <button type="button" className="cmp-link" onClick={() => onShowEntry(entryId)}>{t('companion.showInBinder')}</button>}
          {entryId != null && <button type="button" className="cmp-link" onClick={() => openSheet({ entry_id: entryId }, { name: e.name, printing: e.printing, variantLabel: e.variant_label, number: e.number })}>{t('companion.askAboutThis')}</button>}
        </div>
      </div>
    </div>
  );
}

const SUMMARY_LABEL_KEYS = {
  copies: 'companion.summary.copies', unique_cards: 'companion.summary.unique_cards', unpriced_copies: 'companion.summary.unpriced_copies',
  unknown_quote_date_copies: 'companion.summary.unknown_quote_date_copies', priced_copies: 'companion.summary.priced_copies',
  new_cards: 'companion.tag.new_card', new_finishes: 'companion.tag.new_finish', duplicates: 'companion.tag.duplicate',
  owned: 'companion.summary.owned', total: 'companion.summary.total', missing: 'companion.summary.missing'
};
const SUMMARY_HIDDEN = new Set(['as_of', 'newest_as_of', 'values_by_currency', 'price_sources', 'lines', 'counts', 'entries', 'source', 'type', 'facts_snapshot_id', 'estimate_cad', 'fx']);

export function SummaryCard({ data, title }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const d = data || {};
  const label = (k) => (SUMMARY_LABEL_KEYS[k] ? t(SUMMARY_LABEL_KEYS[k]) : String(k).replace(/_/g, ' '));
  const lines = Array.isArray(d.lines)
    ? d.lines
    : Object.entries(d).filter(([k, v]) => !SUMMARY_HIDDEN.has(k) && ['string', 'number', 'boolean'].includes(typeof v)).map(([k, v]) => ({ label: label(k), value: typeof v === 'boolean' ? (v ? t('common.yes') : t('common.no')) : v }));
  const cad = cadEstimate(d);
  const originals = originalValues(d);
  const sources = formatPriceSources(d.price_sources) || null;
  const fxDate = cad && cad.fxAsOf ? formatDate(cad.fxAsOf) : '';
  const hasDetails = originals.length > 0 || !!sources;
  return (
    <div className="cmp-summary">
      {title && <div className="cmp-card-title">{title}</div>}
      {cad && (
        <div className="cmp-summary-cad">
          <div className="cmp-summary-cad-value">{money(cad.amount, 'CAD')} <span className="cmp-asof">{t('companion.estimate')}</span></div>
          <div className="cmp-asof">
            {cad.priced !== null && cad.total !== null ? (cad.partial ? t('dash.pricedOf', { priced: cad.priced.toLocaleString(), total: cad.total.toLocaleString() }) : t('dash.pricedAll', { total: cad.total.toLocaleString() })) : ''}
            {fxDate ? ` · ${t('dash.fxAsOf', { date: fxDate })}` : ''}{cad.stale ? ` · ${t('companion.estimateStale')}` : ''}
          </div>
        </div>
      )}
      {d.counts && <div className="cmp-counts">{Object.entries(d.counts).map(([k, v]) => <div key={k} className="cmp-count"><div className="cmp-count-num">{v}</div><div className="cmp-count-label">{label(k)}</div></div>)}</div>}
      {lines.map((l, i) => <div key={i} className="cmp-summary-line"><span className="cmp-summary-label">{l.label}</span><span className="cmp-summary-value">{String(l.value ?? '')}</span>{l.hint && <span className="cmp-asof">{l.hint}</span>}</div>)}
      {hasDetails && (
        <button type="button" className="cmp-link" aria-expanded={open} onClick={() => setOpen(o => !o)}>{open ? <ChevronUp size={12} /> : <ChevronDown size={12} />} {t('dash.details')}</button>
      )}
      {open && hasDetails && (
        <div className="cmp-price-details">
          {originals.map(v => <div key={v.currency} className="cmp-summary-line"><span className="cmp-summary-label">{t('dash.originalQuotes')} ({v.currency})</span><span className="cmp-summary-value">{money(v.amount, v.currency)}</span></div>)}
          {originals.length > 1 && <div className="cmp-asof">{t('companion.opening.separateCurrencies')}</div>}
          {sources && <div className="cmp-asof">{sources}</div>}
        </div>
      )}
      {formatDateTime(d.as_of) && <div className="cmp-asof">{t('companion.asOf', { date: formatDateTime(d.as_of) })}</div>}
      {Array.isArray(d.entries) && d.entries.length > 0 && <div className="cmp-entry-list">{d.entries.map((e, i) => <EntryCard key={e.entry_id ?? i} data={e} dense />)}</div>}
    </div>
  );
}

export function ReceiptCard({ receipt, compact = false }) {
  const { t } = useT();
  const { undo } = useCompanion();
  const state = undoState(receipt);
  const lines = diffLines(receipt);
  const doUndo = async () => {
    try { await undo(receipt); }
    catch (err) { window.dispatchEvent(new CustomEvent('cmp-toast', { detail: err?.status === 409 ? t('companion.receipt.changedSince') : (err?.message || t('companion.receipt.undoFailed')) })); }
  };
  return (
    <div className={`cmp-receipt ${compact ? 'compact' : ''} ${state}`}>
      <div className="cmp-receipt-main">
        <span className="cmp-receipt-icon">{state === 'undone' ? <Undo2 size={14} /> : <Check size={14} />}</span>
        <div className="cmp-receipt-text">
          <div className="cmp-receipt-summary">{receipt.summary || t('companion.receipt.changed')}</div>
          {!compact && lines.map(l => <div key={l.field} className="cmp-receipt-diff"><span>{l.field.replace(/_/g, ' ')}</span> {l.from} → <strong>{l.to}</strong></div>)}
          {!compact && Array.isArray(receipt.added_entry_ids) && receipt.added_entry_ids.length > 0 && <div className="cmp-receipt-diff" data-testid="receipt-added">{t('companion.receipt.addedCopies', { count: receipt.added_entry_ids.length, ids: receipt.added_entry_ids.map(id => `#${id}`).join(', ') })}</div>}
          <div className="cmp-asof">{receiptAge(receipt.created_at)}{state === 'undone' ? ` · ${t('companion.receipt.undone')}` : ''}</div>
        </div>
      </div>
      {state === 'undoable' && <button type="button" className="btn btn-secondary cmp-btn-sm" onClick={doUndo}><Undo2 size={14} /> {t('companion.undo')}</button>}
      {state === 'locked' && !receipt.undone_at && <span className="cmp-asof">{t('companion.receipt.notUndoable')}</span>}
    </div>
  );
}

export function ProposalCard({ proposal, onApply, onDismiss }) {
  const { t } = useT();
  const p = proposal || {};
  const patch = p.patch || {};
  const canApply = p.entry_id != null && patch && Object.keys(patch).length > 0;
  return (
    <div className="cmp-proposal">
      <div className="cmp-proposal-head"><AlertTriangle size={14} /> {t('companion.proposal.title')}</div>
      <div className="cmp-proposal-body">
        {p.summary || p.message || Object.entries(patch).map(([k, v]) => `${k.replace(/_/g, ' ')} → ${String(v)}`).join(', ')}
        {p.entry_id != null && <span className="cmp-asof"> · {t('companion.proposal.copy', { id: p.entry_id })}</span>}
      </div>
      <div className="cmp-proposal-actions">
        {canApply && <button type="button" className="btn btn-primary cmp-btn-sm" onClick={onApply}>{t('companion.proposal.apply')}</button>}
        <button type="button" className="btn btn-secondary cmp-btn-sm" onClick={onDismiss}>{t('companion.proposal.dismiss')}</button>
      </div>
    </div>
  );
}

export function AnswerCard({ card }) {
  const c = normalizeCard(card);
  switch (c.type) {
    case 'entry': return <EntryCard data={c.data} title={c.title} />;
    case 'completion': return <CompletionRings data={c.data} setName={c.title} />;
    case 'quiz': return <QuizCard initial={c.data} />;
    default: return <SummaryCard data={c.data} title={c.title} />;
  }
}
